import React from "react";
import Markdown from "react-markdown";
import { markdownUrlTransform } from "utils/markdownUrlTransform";
import Gutter, { GutterSizes } from "../../../ui-kit/Gutter";
import { Typography, TypographyVariant } from "ui-kit";
import styles from "./TextFieldsV2.module.css";
import { FC } from "react";

type TextFieldsV2Props = {
  title?: string | null;
  titleGradient?: boolean;
  intro?: string | null;
  text?: string | null;
  disclaimer?: string | null;
  center?: boolean;
  variantTitle?: TypographyVariant;
  variantText?: TypographyVariant;
  gutterGap?: GutterSizes;
  gutterBottom?: GutterSizes;
};

const TextFieldsV2: FC<TextFieldsV2Props> = ({
  title,
  titleGradient,
  intro,
  text,
  disclaimer,
  center,
  variantTitle = "headingLg",
  variantText = "bodyLg",
  gutterGap = 2,
  gutterBottom = 5,
}) => {
  // Apply gradient to the last word of the title
  const renderTitle = () => {
    if (!title) return null;

    if (!titleGradient) {
      return <Typography variant={variantTitle}>{title}</Typography>;
    }

    const lines = title.split("\n");
    const lastLine = lines[lines.length - 1];
    const lastSpaceIndex = lastLine.lastIndexOf(" ");
    const lastWord = lastSpaceIndex === -1 ? lastLine : lastLine.slice(lastSpaceIndex + 1);
    const restOfLastLine = lastSpaceIndex === -1 ? "" : lastLine.slice(0, lastSpaceIndex + 1);

    return (
      <Typography variant={variantTitle}>
        {lines.slice(0, -1).map((part, i) => (
          <React.Fragment key={i}>
            {part}
            <br />
          </React.Fragment>
        ))}
        {restOfLastLine}
        <span className={styles.titleGradient}>{lastWord}</span>
      </Typography>
    );
  };

  return (
    <div
      className={styles.textContainer}
      style={center ? { textAlign: "center" } : {}}
    >
      {renderTitle()}
      {intro && (
        <>
          <Gutter size={gutterGap} />
          <Typography variant="bodyLg" className={styles.intro}>
            <strong>{intro}</strong>
          </Typography>
        </>
      )}
      {text && (
        <>
          <Gutter size={gutterGap} />
          <Typography variant={variantText} component="div" className={styles.text}>
            {text.split("\n").map((section, index) =>
              section ? (
                <Markdown
                  key={section}
                  allowedElements={["span", "strong", "em", "a"]}
                  unwrapDisallowed
                  className={styles.markdown}
                  urlTransform={markdownUrlTransform}
                >
                  {section}
                </Markdown>
              ) : (
                <br key={index} />
              )
            )}
          </Typography>
        </>
      )}
      {disclaimer && (
        <>
          <Gutter size={gutterGap} />
          <Typography variant="captionSmRegular" className={styles.disclaimer}>
            {disclaimer}
          </Typography>
        </>
      )}
      {(title || intro || text || disclaimer) && (
        <Gutter size={gutterBottom} />
      )}
    </div>
  );
};

export default TextFieldsV2;
